import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, CircularProgress, Alert, Box } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQrCodeAccess } from '../../utils/useQrCodeAccess';
import { buildApiUrl } from '../../config/api';

export default function ReviewPage() {
    const { loading, error: qrError, participantId, envelopeId, tokenValid, token, uuid } = useQrCodeAccess();
    const navigate = useNavigate();
    const location = useLocation();
    const { responseText, envelopeFilename, responseFilename } = location.state || {};
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const envelopeShort = uuid ? uuid.split('-')[0] : '';

    const [accessDenied, setAccessDenied] = useState(false);
    useEffect(() => {
        async function validateAccess() {
            if (tokenValid && participantId && uuid) {
                // 1. Lê a chave digitada pelo participante
                let accessKey = '';
                try {
                    accessKey = localStorage.getItem(`access_key_${uuid}`) || '';
                } catch {}

                // 2. Lê o nickname salvo no objeto qrcode
                let savedNickname = '';
                let qrcodeData = null;
                try {
                    qrcodeData = JSON.parse(localStorage.getItem(`qrcode_${uuid}`));
                    savedNickname = qrcodeData?.nickname || '';
                } catch {}

                // 3. Se não houver nickname salvo, buscar do backend e salvar
                if (!savedNickname) {
                    try {
                        const resp = await fetch(buildApiUrl(`qrcode/${uuid}`));
                        if (resp.ok) {
                            const data = await resp.json();
                            savedNickname = data.nickname || '';
                            // Atualiza localStorage
                            const updatedQrcode = { ...(qrcodeData || {}), nickname: savedNickname };
                            localStorage.setItem(`qrcode_${uuid}` , JSON.stringify(updatedQrcode));
                        }
                    } catch {}
                }

                // 4. Recarrega nickname após possível atualização
                try {
                    qrcodeData = JSON.parse(localStorage.getItem(`qrcode_${uuid}`));
                    savedNickname = qrcodeData?.nickname || '';
                } catch {}

                // 5. Valida: access_key_${uuid} === nickname
                if (!accessKey || !savedNickname || accessKey !== savedNickname) {
                    setAccessDenied(true);
                }
            }
        }
        validateAccess();
    }, [tokenValid, participantId, uuid]);

    if (loading) {
        return <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}><CircularProgress /></Container>;
    }
    if (qrError || tokenValid === false || accessDenied) {
        return (
            <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
                <Alert severity="error">{qrError || accessDenied ? 'Chave de acesso não informada ou incorreta.' : 'Token inválido. Acesso não autorizado.'}</Alert>
            </Container>
        );
    }
    if (tokenValid === null) {
        return null;
    }

    const envelopeImageUrl = envelopeFilename ? buildApiUrl(`images/${envelopeFilename}`) : null;
    const responseImageUrl = responseFilename ? buildApiUrl(`images/${responseFilename}`) : null;

    const handleConfirm = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(buildApiUrl(`submit/confirm/${participantId}/${envelopeId}`), { method: 'POST' });
            if (!response.ok) {
                let errorMsg = `Erro ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.detail || errorMsg;
                } catch (e) { /* Ignore */ }
                throw new Error(errorMsg);
            }
            const result = await response.json();
            navigate(`/complete/${uuid}?token=${token}`, { state: { targetText: result.target_text } });
        } catch (err) {
            setError(err.message);
            setIsLoading(false);
        }
    };

    const handleEditText = () => {
        navigate(`/envelope/${uuid}`, { state: { ...location.state, isEditing: true, editingText: responseText } });
    };

    const handleEditPhotos = () => {
        navigate(`/photos/${uuid}`, { state: { ...location.state, isEditing: true } });
    };

    if (!responseText || !envelopeFilename || !responseFilename) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                 <Typography variant="h5" gutterBottom>Revisão - Sessão Envelope {envelopeShort}</Typography>
                 {error ? (
                    <Alert severity="error">{error}</Alert>
                 ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5}}><CircularProgress /></Box>
                 )}
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>Revisão - Sessão Envelope {envelopeShort}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #eee', borderRadius: '4px' }}>
                <Typography variant="h6">Texto Digitado:</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{responseText || "(Texto não disponível)"}</Typography>
                <Button size="small" startIcon={<Edit />} onClick={handleEditText} disabled={isLoading}>Editar Texto</Button>
            </Box>
            <Box sx={{ mb: 3, p: 2, border: '1px solid #eee', borderRadius: '4px' }}>
                <Typography variant="h6">Fotos Enviadas:</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 1, mb: 1, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2">Envelope</Typography>
                        {envelopeImageUrl ? <img src={envelopeImageUrl} alt="Envelope" style={{ maxHeight: '100px', maxWidth: '100%' }} /> : "(Foto não disponível)"}
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2">Resposta</Typography>
                        {responseImageUrl ? <img src={responseImageUrl} alt="Resposta" style={{ maxHeight: '100px', maxWidth: '100%' }} /> : "(Foto não disponível)"}
                    </Box>
                </Box>
                <Button size="small" startIcon={<Edit />} onClick={handleEditPhotos} disabled={isLoading}>Alterar Fotos</Button>
            </Box>
            <Button
                variant="contained"
                color="success"
                onClick={handleConfirm}
                disabled={isLoading || !!error}
                fullWidth
            >
                {isLoading ? <CircularProgress size={24} /> : 'Confirmar e Ver Texto Alvo'}
            </Button>
        </Container>
    );
}
